/**
 * The guest's side of an event that charges rooms from its per-night room prices (the public event carries
 * `room_charge` only when the organizer turned that on). What a room costs comes from the server — its per-night
 * range and stay total here, and the charge itself in the quote; nothing in this file works out money.
 */
import { money, dateRange } from '@/lib/format';
import { initialQuantities, type PublicItem, type PriceLine } from './money';

export type RoomError = 'room_type_required' | 'hotel_required' | 'room_unavailable' | 'room_unpriced';
/** One room type a guest may book at one hotel. An unpriced type carries no cents. */
export interface RoomChargeType { type: string; nights: number; priced: boolean; per_night_min_cents: number | null; per_night_max_cents: number | null; stay_total_cents: number | null }
export interface RoomChargeHotel { hotel_id: string; types: RoomChargeType[] }
export interface RoomCharge { on: boolean; hotels: RoomChargeHotel[] }
/** A reservation item as the public event sends it: `charged: false` marks a room item that is kept but not charged or shown. */
export type ChargeableItem = PublicItem & { charged?: boolean };
export interface RoomChoice extends RoomChargeType { label: string }

/** The event's room charge when it is on; null for every other event (the key is absent, and the page is today's page). */
export function roomChargeOf(event: { room_charge?: RoomCharge | null }): RoomCharge | null {
  const rc = event.room_charge;
  return rc && rc.on === true && Array.isArray(rc.hotels) ? rc : null;
}

/**
 * The room types a guest can choose at one hotel: that hotel's own priced types, each under that hotel's own name.
 * Two hotels that call a type the same thing stay two choices — the price belongs to the (hotel, type) pair.
 */
export function chargedTypesFor(rc: RoomCharge, hotelId: string | null | undefined, labelOf: (hotelId: string, type: string) => string): RoomChoice[] {
  const hotel = hotelId ? rc.hotels.find((h) => h.hotel_id === hotelId) : undefined;
  return (hotel?.types ?? []).filter((t) => t.priced && t.stay_total_cents != null).map((t) => ({ ...t, label: labelOf(hotel!.hotel_id, t.type) }));
}

/** "$219.00 per night", or "$199.00–$239.00 per night" when the nights differ. */
export function perNightLabel(t: RoomChargeType): string {
  const min = t.per_night_min_cents ?? 0;
  const max = t.per_night_max_cents ?? min;
  return min === max ? `${money(min)} per night` : `${money(min)}–${money(max)} per night`;
}

/** "3 nights, Nov 13 – 16, 2031 · $580.00 for the room" */
export function stayLine(t: RoomChargeType, start: string, end: string): string {
  return `${t.nights === 1 ? '1 night' : `${t.nights} nights`}, ${dateRange(start, end)} · ${money(t.stay_total_cents ?? 0)} for the room`;
}

/** The shown items; the positions in the quantities sent to the server never move. */
export const isShown = (item: ChargeableItem) => item.charged !== false;

/** Starting quantities, one per reservation item: an item that is not charged keeps its slot, at 0. */
export function startQuantities(items: readonly ChargeableItem[]): number[] {
  return initialQuantities(items as PublicItem[]).map((q, i) => (isShown(items[i]!) ? q : 0));
}

/** The room line of a quote or a stored price, as the server named it. */
export function roomLineOf(lines: readonly PriceLine[] | null | undefined): PriceLine | null {
  return (lines ?? []).find((l) => l.item_id === 'room_price') ?? null;
}
